import { useEffect } from 'react';

import { MailOutlined } from '@ant-design/icons';
import { Col, Row, Form } from 'antd';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { forgotPassword, clearForgotPassword } from 'src/actions';
import { MInput, MButton, CardLayout, MTypography } from 'src/lib';
import { Color, Spacing } from 'src/styles';
import * as Yup from 'yup';

import * as Styles from '../BaseLayout.styles';

import { mailOpenIcon } from './ForgotPassword.styles';

export const forgotPasswordValidationSchema = Yup.object().shape({
  email: Yup.string().required('Email is required').email('Email is invalid'),
});

interface ForgotPasswordFormValues {
  email: string;
}

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isSucceeded = useSelector((state: any) => Boolean(state.user.forgotPassword.__succeeded));
  const isRequested = useSelector((state: any) => Boolean(state.user.forgotPassword.__requested));

  const renderForgotPasswordForm = (isRequested: boolean, forgotPassword: (email: string) => void) => {
    return (
      <Formik<ForgotPasswordFormValues>
        initialValues={{
          email: '',
        }}
        validationSchema={forgotPasswordValidationSchema}
        onSubmit={values => forgotPassword(values.email)}>
        {form => {
          return (
            <Form autoComplete='off' style={{ width: '100%' }}>
              <Row justify='center'>
                <MTypography variant='h6' weight='bold' color={Color.Gray9} className={Styles.formTitle}>
                  Forgot Password
                </MTypography>
              </Row>
              <Row className={Spacing.my16}>
                <Col span={24}>
                  <MInput
                    placeholder='Enter your email'
                    onChange={(value: string) => form.setFieldValue('email', value)}
                    value={form.values.email}
                    error={form.errors.email}
                    autoComplete='off'
                    name='forgot-password-email-name-attribute'
                  />
                </Col>
              </Row>

              <Row justify='center' className={Spacing.my16}>
                <MButton
                  type='secondary'
                  loading={isRequested}
                  disabled={!form.isValid}
                  onClick={() => {
                    form.submitForm();
                  }}>
                  Reset Password
                </MButton>
              </Row>
            </Form>
          );
        }}
      </Formik>
    );
  };

  const renderForgotPasswordSubmitted = (navigate: NavigateFunction) => {
    return (
      <>
        <Row justify='center'>
          <div className={Styles.iconContainer}>
            <MailOutlined className={mailOpenIcon} />
          </div>
        </Row>
        <Row justify='center'>
          <MTypography
            variant='subtitle1'
            weight='bold'
            align='center'
            color={Color.Green7}
            className={Styles.afterActionTitle}>
            Check your email
          </MTypography>
        </Row>
        <Row justify='center'>
          <MTypography variant='body1' align='center' color={Color.Gray7} className={Styles.afterActionSubtitle}>
            We have sent you an email with password reset instructions
          </MTypography>
        </Row>

        <Row justify='center'>
          <MButton
            type='secondary'
            onClick={() => {
              navigate('/login');
            }}>
            Go To Log In
          </MButton>
        </Row>
      </>
    );
  };

  useEffect(() => {
    return () => {
      dispatch(clearForgotPassword());
    };
  }, []);

  return (
    <CardLayout
      subtitle=' Please enter the email address for your account. An email with instructions will be sent to you. Once you have
    received the email, you will be able to choose a new password for your account.'>
      {!isSucceeded
        ? renderForgotPasswordForm(isRequested, (email: string) => dispatch(forgotPassword({ email })))
        : renderForgotPasswordSubmitted(navigate)}
    </CardLayout>
  );
};
